import type { USCounty, USState } from '@/shared/api/geographyApi';
import type { SearchFilters } from '../types';
import type { LocationData } from './geocodedLocation';

export type UrlLocationQuery = {
  countyId?: string;
  county?: string;
  city?: string;
  state?: string;
};

export type UrlLocationDeps = {
  getStates: () => Promise<USState[]>;
  getCounties: (stateCode?: string) => Promise<USCounty[]>;
  geocodeCity: (address: string) => Promise<LocationData | null>;
};

type ResolvedLocation = Pick<SearchFilters, 'countyId' | 'stateCode' | 'locationAddress'>;

const normalize = (text: string) => text.trim().toLowerCase();

// Any failing lookup only skips its own step.
const attempt = async <T>(lookup: () => Promise<T>): Promise<T | undefined> => {
  try {
    return await lookup();
  } catch {
    return undefined;
  }
};

const countyLocation = (county: USCounty, fallbackState?: USState): ResolvedLocation => ({
  countyId: county.id,
  stateCode: county.stateCode ?? fallbackState?.code,
  locationAddress: [county.name, county.stateCode].filter(Boolean).join(', '),
});

/**
 * Resolves the location params of a search link into the filter the location
 * box would set (county and/or state plus a display address). The most
 * specific param that resolves wins: countyId, county name, city, then state.
 * Anything that doesn't resolve is dropped, falling back to the state, then to
 * no location at all.
 */
export const resolveUrlLocation = async (
  query: UrlLocationQuery,
  deps: UrlLocationDeps,
): Promise<ResolvedLocation> => {
  if (!query.countyId && !query.county && !query.city && !query.state) return {};

  const states = (await attempt(deps.getStates)) ?? [];
  const wanted = query.state && normalize(query.state);
  const state = wanted
    ? states.find((s) => normalize(s.code) === wanted || normalize(s.name) === wanted)
    : undefined;

  if (query.countyId || query.county) {
    // An id is exact, so it is looked up across every county; the state only
    // narrows a county *name*.
    const counties =
      (await attempt(() => deps.getCounties(query.countyId ? undefined : state?.code))) ?? [];
    const byId = counties.find((c) => c.id === query.countyId);
    if (byId) return countyLocation(byId, state);

    if (query.county) {
      const name = normalize(query.county);
      const matches = counties.filter(
        (c) =>
          (!state || c.stateCode === state.code) &&
          (normalize(c.name) === name || normalize(c.name) === `${name} county`),
      );
      // A name like "Washington County" exists in many states; only take a unique match.
      if (matches.length === 1) return countyLocation(matches[0], state);
    }
  }

  if (query.city) {
    const place = await attempt(() =>
      deps.geocodeCity(state ? `${query.city}, ${state.code}` : query.city!),
    );
    // Google may pick a same-named city elsewhere; reject it if it left the requested state.
    if (place?.stateCode && (!state || place.stateCode === state.code)) {
      return {
        countyId: place.countyId,
        stateCode: place.stateCode,
        locationAddress: place.address,
      };
    }
  }

  return state ? { stateCode: state.code, locationAddress: state.name } : {};
};
